import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import { formatErr, fmtDateTime } from "@/lib/api";
import { dapi as api } from "./deskApi";

const plural = (n) => `${n} ${n === 1 ? "call" : "calls"}`;
const money = (usd) => (usd < 0.01 && usd > 0 ? `$${usd.toFixed(4)}` : `$${usd.toFixed(2)}`);
const cred = (c) => (c < 0.01 && c > 0 ? c.toFixed(4) : c.toFixed(2));
const Cost = ({ r }) => <span className="tabular-nums"><b>{cred(r.credits)}</b> cr <span className="text-slate-500">· {money(r.usd)}</span></span>;

const Card = ({ label, value, testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-lg font-semibold text-slate-900 mt-0.5">{value}</div>
  </div>
);

const Groups = ({ rows }) => {
  const groups = useMemo(() => {
    const g = {};
    rows.forEach((r) => {
      g[r.group] = g[r.group] || { group: r.group, calls: 0, credits: 0, usd: 0, items: [] };
      Object.assign(g[r.group], { calls: g[r.group].calls + r.calls, credits: g[r.group].credits + r.credits, usd: g[r.group].usd + r.usd });
      g[r.group].items.push(r);
    });
    return Object.values(g).sort((a, b) => b.credits - a.credits);
  }, [rows]);
  if (!groups.length) return <p className="text-sm text-slate-500 p-3">No AI calls in this period yet.</p>;
  return (
    <div className="divide-y divide-slate-100" data-testid="ai-usage-by-kind">
      {groups.map((g) => (
        <div key={g.group} className="py-2" data-testid={`ai-usage-group-${g.group}`}>
          <div className="flex items-center justify-between text-sm font-semibold text-slate-900">
            <span>{g.group} <span className="text-xs font-normal text-slate-500">· {plural(g.calls)}</span></span><Cost r={g} />
          </div>
          {g.items.map((f) => (
            <div key={f.feature} className="flex items-center justify-between text-xs text-slate-600 pl-3 mt-1">
              <span>{f.feature} · {f.calls}</span><Cost r={f} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const Calls = ({ calls }) => (
  <table className="w-full text-xs" data-testid="ai-usage-calls">
    <thead className="text-slate-500 text-left sticky top-0 bg-slate-50">
      <tr><th className="py-1 px-2">When</th><th className="px-2">Kind</th><th className="px-2">For</th><th className="px-2">Model</th><th className="px-2 text-right">Tokens in/out</th><th className="px-2 text-right">Cost</th></tr>
    </thead>
    <tbody>
      {calls.map((c) => (
        <tr key={c.id} className="border-t border-slate-100" data-testid={`ai-usage-call-${c.id}`}>
          <td className="py-1 px-2 whitespace-nowrap">{fmtDateTime(c.at)}</td>
          <td className="px-2">{c.group} · {c.feature}</td>
          <td className="px-2 max-w-[220px] truncate" title={c.ref?.label || ""}>{c.ref?.label || "—"}</td>
          <td className="px-2 whitespace-nowrap">{c.model}</td>
          <td className="px-2 text-right tabular-nums">{c.input_tokens.toLocaleString()} / {c.output_tokens.toLocaleString()}</td>
          <td className="px-2 text-right whitespace-nowrap"><Cost r={c} /></td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Owner-only: what the app's AI (Universal Key) cost, by kind, model, day and each call.
export default function AiUsageApp() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("kind");
  const load = () => {
    setData(null); setErr("");
    api.get("/ai-usage", { params: { days } }).then(({ data: d }) => setData(d)).catch((e) => setErr(formatErr(e.response?.data?.detail) || "Couldn't load AI usage."));
  };
  useEffect(load, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  const tabBtn = (k, label) => (
    <button type="button" onClick={() => setTab(k)} className={`px-3 py-1 text-xs rounded-full ${tab === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`} data-testid={`ai-usage-tab-${k}`}>{label}</button>
  );
  return (
    <div className="h-full flex flex-col bg-slate-50 text-slate-800" data-testid="ai-usage-app">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-white">
        <Sparkles className="w-4 h-4 text-violet-600" />
        <span className="font-semibold text-sm">AI Usage</span>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="ml-auto border border-slate-300 rounded px-2 py-1 text-xs" data-testid="ai-usage-days">
          {[[1, "Last 24 hours"], [7, "Last 7 days"], [30, "Last 30 days"], [90, "Last 90 days"], [365, "Last year"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button type="button" onClick={load} className="p-1.5 rounded hover:bg-slate-100" title="Refresh" data-testid="ai-usage-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {err && <p className="m-4 text-sm text-red-600" data-testid="ai-usage-error">{err}</p>}
      {!data && !err && <Loader2 className="w-6 h-6 animate-spin text-slate-400 m-auto" />}
      {data && (
        <div className="flex-1 min-h-0 flex flex-col gap-3 p-4">
          <div className="grid grid-cols-3 gap-2">
            <Card label="AI calls" value={data.total.calls.toLocaleString()} testId="ai-usage-total-calls" />
            <Card label="Credits" value={cred(data.total.credits)} testId="ai-usage-total-credits" />
            <Card label="Dollars" value={money(data.total.usd)} testId="ai-usage-total-usd" />
          </div>
          <p className="text-[11px] text-slate-500">
            1 USD = {data.credits_per_usd} credits. Worked out from each call&apos;s real token counts at Universal Key prices, so it may differ slightly from your Emergent bill.
            {data.tracking_since ? ` Counting since ${fmtDateTime(data.tracking_since)}.` : " Counting starts with the next AI call."}
          </p>
          <div className="flex gap-2">{tabBtn("kind", "By kind")}{tabBtn("model", "By model")}{tabBtn("day", "By day")}{tabBtn("calls", "Every call")}</div>
          <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-slate-200 bg-white px-3">
            {tab === "kind" && <Groups rows={data.by_feature} />}
            {tab === "model" && data.by_model.map((m) => <div key={m.model} className="flex justify-between text-sm py-2 border-b border-slate-100"><span>{m.model} · {plural(m.calls)}</span><Cost r={m} /></div>)}
            {tab === "day" && data.by_day.map((d) => <div key={d.day} className="flex justify-between text-sm py-2 border-b border-slate-100"><span>{d.day} · {plural(d.calls)}</span><Cost r={d} /></div>)}
            {tab === "calls" && <Calls calls={data.calls} />}
          </div>
        </div>
      )}
    </div>
  );
}
